import { ReactElement, useEffect, useState } from "react";
import {
  Autocomplete,
  FormControl,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from "@mui/material";
import StyledTableRow from "../../../../../components/styledTableRow";
import { Add, Delete } from "@mui/icons-material";
import { getPalette } from "../../../../../configuration/configuration";
import { getUID } from "../../../../../system/utils";
import { useLazyFetch } from "../../../../../system/useFetch";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TableContainer from "../../../../../components/tableContainer";
import { IEventVariable, IDashboardComponent, IEventConfig, IEventCondition } from "../../../types";
import { IAcquisitionVariableFullData } from "../../../../../types";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ConditionEditor from "./conditionEditor";

interface IProps {
  component: IDashboardComponent<IEventConfig>;
  updateComponent: (component: IDashboardComponent<IEventConfig>) => void;
  zoneID: number;
}

const maxVar = 15;

interface IVariableRowProps {
  index: number;
  machines: string[];
  machineVariables: Record<string, IAcquisitionVariableFullData[]>;
  data: IEventVariable;
  setData: (data: IEventVariable) => void;
  onRemoveVariable: (index: number) => void;
}

interface IDialogContext {
  open: boolean;
  selectedVariable: IEventVariable | null;
  metaData: IAcquisitionVariableFullData | null;
}


const VariableRow = ({ index, machineVariables, machines, data, setData, onRemoveVariable }: IVariableRowProps): ReactElement => {
  const found = machineVariables[data.machine]?.find(x => x.code === data.code) ?? null;
  const [ dialogContext, setDialogContext ] = useState<IDialogContext>({ open: false, selectedVariable: null, metaData: null });

  const updateItemsSeries = (field: keyof IEventVariable, value: any) => {

    if (field === "code") {
      data[field] = value;
    } else if (field === "machine"){
      data.machine = value;
      data.code = "";
    }

    setData(data);
  };

  const onAccept = (conditions: IEventCondition[]) => {
    data.conditions = conditions;
    setData({ ...data });
    setDialogContext({ ...dialogContext, open: false });
  };

  const onCancel = () => {
    setDialogContext({ ...dialogContext, open: false });
  };

  return (
    <StyledTableRow hover={true} role="checkbox" tabIndex={-1} >
      <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
        {index + 1}
      </TableCell>

      <TableCell>
        <FormControl className="w-100">
          <Select
            variant="standard"
            value={data.machine}
            displayEmpty={true}
            onChange={(event: SelectChangeEvent) => updateItemsSeries("machine", event.target.value as string)}>
            { machines.map(machine => {
              return <MenuItem key={`unit-${index}-${machine}`} value={machine}>{machine}</MenuItem>;
            })}
          </Select>
        </FormControl>
      </TableCell>

      <TableCell style={{ minWidth: 180, maxWidth: 250 }}>
        <Autocomplete
          options={machineVariables[data.machine]}
          getOptionLabel={(option: IAcquisitionVariableFullData) => option.name}
          value={found}
          openOnFocus={true}
          onChange={(_event: any, newValue: IAcquisitionVariableFullData | null) => {
            updateItemsSeries("code", newValue?.code);
          }}
          renderInput={(params) =>
            <TextField {...params} variant="standard" color={"primary"}/>
          }
        />
      </TableCell>

      <TableCell>
        <IconButton style={{ padding: 0, margin: 0 }}
          disabled={data.code === ""}
          onClick={() => setDialogContext({ open: true, selectedVariable: data, metaData: found })}>
          <Tooltip title={"Config"}>
            <MoreVertIcon style={{ color: getPalette().statusColor.info.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>

      <TableCell>
        <IconButton style={{ padding: 0, margin: 0 }} onClick={() => onRemoveVariable(index)}>
          <Tooltip title={"Remove variable"}>
            <Delete style={{ color: getPalette().statusColor.error.background }}/>
          </Tooltip>
        </IconButton>
      </TableCell>

      {dialogContext.open && dialogContext.selectedVariable && dialogContext.metaData &&
        <ConditionEditor
          data={data}
          machineVariables={machineVariables[data.machine]}
          onAccept={onAccept}
          onCancel={onCancel}
        />}
    </StyledTableRow>
  );
};

const EventConfigPanel = ({ component, updateComponent, zoneID }: IProps): ReactElement => {
  const [ getMachineVariables, { data } ] = useLazyFetch<IAcquisitionVariableFullData[]>();
  const [ machineVariables, setMachineVariables ] = useState<Record<string, IAcquisitionVariableFullData[]>>({});
  const [ machines, setMachines ] = useState<string[]>([]);

  const variablesLength = component.config.variables.length;

  useEffect(() => {
    getMachineVariables({
      module: "acquisition",
      query: "getVariablesByZone",
      payload: {
        zoneID
      }
    });
  }, [ zoneID ]);

  useEffect(() => {
    const dictionary: Record<string, IAcquisitionVariableFullData[]> = {};

    (data ?? []).forEach(v => {
      if (dictionary[v.machine.name]) {
        dictionary[v.machine.name].push(v);
      } else {
        dictionary[v.machine.name] = [v];
      }
    });

    setMachineVariables(dictionary);
    setMachines(Object.keys(dictionary));

    if (data) {
      updateComponent({ ...component });
    }

  }, [ data ]);

  const addSeries = () => {
    component.config.variables.push({
      hash: getUID(),
      machine: machines[0],
      code: "",
      conditions: []
    });

    updateComponent({ ...component });
  };

  const onRemoveVariable = (index: number) => {
    component.config.variables.splice(index, 1);
    updateComponent({ ...component });
  };

  return (
    <TableContainer sx={{ maxHeight: 570, width: 500 }}>
      <Table stickyHeader={true} size={"small"} >
        <TableHead>
          <TableRow>
            <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
              <IconButton style={{ padding: 0, margin: 0, maxWidth: 21 }} onClick={addSeries} disabled={variablesLength >= maxVar || machines.length === 0}>
                <Tooltip title={"Add variable"}>
                  <Add style={{ color: getPalette().statusColor.info.background }}/>
                </Tooltip>
              </IconButton>
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 100, maxWidth: 100 }}>
              Machine
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 180, maxWidth: 250 }}>
              Variable - {variablesLength} / {maxVar}
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 50, width: 50 }}>Config</TableCell>
            <TableCell className="table-header" />
          </TableRow>
        </TableHead>
        <TableBody>
          {data && machines.length > 0 && component.config.variables.map((row, index) => {
            return (
              <VariableRow key={`series-row-${index}`}
                onRemoveVariable={onRemoveVariable}
                machineVariables={machineVariables}
                machines={machines} data={row} index={index} setData={() => updateComponent({ ...component })} />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EventConfigPanel;
